import { useEffect, useState } from "react";
import {
  ArrowLeft, Loader2, Users, BookText, DollarSign, Layers, Gift, Ticket, Plus, Trash2, Power, CheckCircle2, Crown,
  Coins, Sparkles, Repeat, Percent, TrendingUp, Award, Download, Search, ToggleLeft, ToggleRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { AiCosts } from "@/components/AiCosts";
import { PipelineKey } from "@/components/PipelineKey";
import { GenerationLimit } from "@/components/GenerationLimit";
import { ContinuityFlags } from "@/components/ContinuityFlags";
import { CoverBlindTest } from "@/components/CoverBlindTest";
import { AdminInbox } from "@/components/AdminInbox";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return "—"; } };
const fmtDay = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return d; } };

const Stat = ({ icon: Icon, label, value, sub, accent = "steel" }) => (
  <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
    <div className={`flex items-center gap-2 text-${accent}-300 mb-2`}>
      <Icon className="w-4 h-4" /><span className="font-ui text-[11px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-display text-3xl font-bold text-slate-100">{value}</div>
    {sub && <div className="font-ui text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const ChartTip = ({ active, payload, label, prefix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gold-500/12 bg-navy-950/95 px-3 py-2 shadow-xl">
      <div className="font-ui text-[11px] text-slate-400 mb-1">{fmtDay(label)}</div>
      {payload.map((p) => (
        <div key={p.name} className="font-ui text-xs text-slate-100 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{prefix}{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
    <div className="flex items-baseline justify-between mb-4">
      <h3 className="font-ui text-sm font-semibold text-slate-200">{title}</h3>
      {subtitle && <span className="font-ui text-[11px] text-slate-500">{subtitle}</span>}
    </div>
    <div className="h-56">{children}</div>
  </div>
);

export const AdminPanel = ({ onBack }) => {
  const [tab, setTab] = useState("overview");
  const [m, setM] = useState(null);
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", kind: "credits", amount: 1, max_redemptions: 0, note: "" });
  const [creating, setCreating] = useState(false);
  const [range, setRange] = useState(30);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [grantingId, setGrantingId] = useState("");

  useEffect(() => { api.adminMetrics().then(setM).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => { if (tab === "promo") api.adminListPromo().then(setPromos).catch(() => {}); }, [tab]);

  const loadUsers = async (q = userQuery) => {
    setLoadingUsers(true);
    try { setUsers(await api.adminListUsers(q, 50)); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoadingUsers(false); }
  };
  useEffect(() => {
    if (tab === "users" && users.length === 0) loadUsers("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const grant = async (user_id, kind, amount, label) => {
    setGrantingId(user_id + kind + amount);
    try {
      const updated = await api.adminGrantUser(user_id, kind, amount);
      setUsers((prev) => prev.map((u) => (u.user_id === user_id ? updated : u)));
      api.adminMetrics().then(setM).catch(() => {});
      toast.success(label || "Updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGrantingId(""); }
  };

  const toggleCredited = async (u) => {
    setGrantingId(u.user_id + "credited");
    try {
      const updated = await api.adminSetCredited(u.user_id, !u.credited);
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? updated : x)));
      api.adminMetrics().then(setM).catch(() => {});
      toast.success(updated.credited ? "Marked credited — +1 book credit" : "Credited removed");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGrantingId(""); }
  };

  const exportCsv = () => {
    const rows = (m?.timeseries || []).slice(-range);
    const header = "date,signups,books,revenue";
    const body = rows.map((r) => `${r.date},${r.users},${r.books},${r.revenue}`).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `klyr-analytics-${range}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const chartData = (m?.timeseries || []).slice(-range);


  const createPromo = async () => {
    if (!form.code.trim()) return;
    setCreating(true);
    try {
      await api.adminCreatePromo({ ...form, amount: Number(form.amount), max_redemptions: Number(form.max_redemptions) });
      setPromos(await api.adminListPromo());
      setForm({ code: "", kind: "credits", amount: 1, max_redemptions: 0, note: "" });
      toast.success("Promo code created");
    } catch (e) { toast.error(apiError(e, "Could not create code.")); }
    finally { setCreating(false); }
  };

  const toggle = async (code) => { await api.adminTogglePromo(code); setPromos(await api.adminListPromo()); };
  const remove = async (code) => { await api.adminDeletePromo(code); setPromos(await api.adminListPromo()); toast.success("Deleted"); };

  const inputCls = "bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400/60";

  return (
    <div data-testid="admin-panel" className="max-w-6xl mx-auto py-8">
      <button data-testid="admin-back" onClick={onBack} className="flex items-center gap-2 font-ui text-sm text-slate-400 hover:text-gold-200 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-2 text-slate-300 mb-1"><Crown className="w-5 h-5" /><span className="font-ui text-xs uppercase tracking-[0.3em]">Admin</span></div>
      <h1 className="font-display text-4xl text-slate-100 mb-6">Control Room</h1>

      <div className="flex gap-2 mb-8">
        {[["overview", "Overview"], ["costs", "AI Costs"], ["users", "Users"], ["promo", "Promo codes"], ["inbox", "Support & careers"], ["pipeline", "Pipeline V2"], ["flags", "Continuity flags"], ["covertest", "Cover test"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`font-ui text-sm font-medium rounded-full px-5 py-2 transition-colors ${tab === k ? "bg-gold-400 text-stone-950" : "text-slate-300 border border-gold-500/12 hover:border-gold-400/60"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (!m ? (
        <div className="min-h-[30vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-overview">
          {/* Primary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={DollarSign} label="Revenue" value={`$${m.revenue.toFixed(2)}`} sub={`$${(m.revenue_30d ?? 0).toFixed(2)} in 30d · ${m.paid_count} payments`} accent="emerald" />
            <Stat icon={Users} label="Users" value={m.users_total} sub={`+${m.new_users_30d ?? 0} in 30d · ${m.subscribers} subscribers`} />
            <Stat icon={BookText} label="Books" value={m.books_total} sub={`+${m.books_30d ?? 0} in 30d · ${m.books_done} done`} />
            <Stat icon={Layers} label="Series" value={m.series_total} sub={`${m.series_active} active`} />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Award} label="Subscribers" value={m.subscribers} sub={`${m.users_total ? Math.round((m.subscribers / m.users_total) * 100) : 0}% of users`} accent="steel" />
            <Stat icon={TrendingUp} label="Avg / user" value={m.avg_books_per_user ?? 0} sub="books per user" />
            <Stat icon={DollarSign} label="Avg order" value={`$${(m.aov ?? 0).toFixed(2)}`} sub={`$${(m.revenue_7d ?? 0).toFixed(2)} last 7d`} accent="emerald" />
            <Stat icon={Coins} label="Credits out" value={m.credits_outstanding ?? 0} sub={`${m.publish_credits_outstanding ?? 0} publish credits`} accent="amber" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={BookText} label="Previews" value={m.books_preview ?? 0} sub={`${m.books_full ?? 0} full books`} />
            <Stat icon={Loader2} label="In progress" value={m.books_in_progress} sub={`${m.books_error} failed`} accent="amber" />
            <Stat icon={Ticket} label="Promo redeemed" value={m.promo_redemptions_total ?? 0} sub={`${m.promo_active ?? 0} active codes`} accent="steel" />
            <Stat icon={Gift} label="Referrals" value={m.referrals_total} sub="credited" />
          </div>

          {/* Over-time charts */}
          <GenerationLimit />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2" data-testid="admin-range-toggle">
              {[[7, "7 days"], [30, "30 days"], [90, "90 days"]].map(([d, l]) => (
                <button key={d} data-testid={`admin-range-${d}`} onClick={() => setRange(d)}
                  className={`font-ui text-xs font-medium rounded-full px-4 py-1.5 transition-colors ${range === d ? "bg-gold-400 text-stone-950" : "text-slate-300 border border-gold-500/12 hover:border-gold-400/60"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button data-testid="admin-export-csv" onClick={exportCsv}
              className="flex items-center gap-2 font-ui text-xs font-medium text-slate-300 border border-gold-500/12 hover:border-gold-400/60 rounded-full px-4 py-1.5 transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <ChartCard title="Signups & books" subtitle={`Last ${range} days`}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7dd3fc" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#7dd3fc" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gBooks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#292524" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#78716c", fontSize: 10 }} interval={Math.max(0, Math.floor(range / 6) - 1)} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#78716c", fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="users" name="Signups" stroke="#7dd3fc" strokeWidth={2} fill="url(#gUsers)" />
                  <Area type="monotone" dataKey="books" name="Books" stroke="#a78bfa" strokeWidth={2} fill="url(#gBooks)" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Revenue" subtitle={`Last ${range} days`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#292524" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "#78716c", fontSize: 10 }} interval={Math.max(0, Math.floor(range / 6) - 1)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#78716c", fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip content={<ChartTip prefix="$" />} cursor={{ fill: "#ffffff08" }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#34d399" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Breakdowns */}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5" data-testid="admin-books-by-mode">
              <h3 className="font-ui text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-slate-300" /> Books by type</h3>
              {(m.books_by_mode || []).length === 0 ? (
                <p className="font-read text-sm text-slate-500 py-3">No books yet.</p>
              ) : (
                <div className="space-y-3">
                  {m.books_by_mode.map((row) => {
                    const pct = m.books_total ? Math.round((row.count / m.books_total) * 100) : 0;
                    return (
                      <div key={row.mode}>
                        <div className="flex items-center justify-between font-ui text-xs text-slate-300 mb-1">
                          <span>{row.label}</span><span className="text-slate-500">{row.count} · {pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-navy-800 overflow-hidden">
                          <div className="h-full rounded-full bg-gold-400" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5" data-testid="admin-revenue-by-product">
              <h3 className="font-ui text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2"><Repeat className="w-4 h-4 text-emerald-300" /> Revenue by product</h3>
              {Object.keys(m.revenue_by_product || {}).length === 0 ? (
                <p className="font-read text-sm text-slate-500 py-3">No revenue yet.</p>
              ) : (
                <div className="divide-y divide-gold-500/12">
                  {Object.entries(m.revenue_by_product).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
                    <div key={name} className="flex items-center justify-between py-2.5">
                      <span className="font-ui text-sm text-slate-200">{name}</span>
                      <span className="font-ui text-sm font-semibold text-emerald-300">${Number(amt).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
              <h3 className="font-ui text-sm font-semibold text-slate-200 mb-3">Recent signups</h3>
              <div className="divide-y divide-gold-500/12">
                {m.recent_users.map((u, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5">
                    <div><div className="font-ui text-sm text-slate-200">{u.email}</div><div className="font-ui text-xs text-slate-500">{fmtDate(u.created_at)}</div></div>
                    <span className={`font-ui text-[11px] px-2 py-0.5 rounded-full ${u.plan === "Unlimited" ? "bg-gold-400/15 text-slate-200" : "bg-navy-800 text-slate-400"}`}>{u.plan}</span>
                  </div>
                ))}
                {m.recent_users.length === 0 && <p className="font-read text-sm text-slate-500 py-3">No users yet.</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
              <h3 className="font-ui text-sm font-semibold text-slate-200 mb-3">Recent payments</h3>
              <div className="divide-y divide-gold-500/12">
                {m.recent_payments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5">
                    <div><div className="font-ui text-sm text-slate-200">{p.item}</div><div className="font-ui text-xs text-slate-500">{p.email} · {fmtDate(p.date)}</div></div>
                    <span className="font-ui text-sm font-semibold text-emerald-300">${p.amount.toFixed(2)}</span>
                  </div>
                ))}
                {m.recent_payments.length === 0 && <p className="font-read text-sm text-slate-500 py-3">No payments yet.</p>}
              </div>
            </div>
          </div>
        </div>
      ))}

      {tab === "costs" && <AiCosts />}

      {tab === "users" && (
        <div className="space-y-5" data-testid="admin-users">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="admin-user-search"
                placeholder="Search by email or name…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadUsers(); }}
                className={`${inputCls} w-full pl-9`}
              />
            </div>
            <button data-testid="admin-user-search-btn" onClick={() => loadUsers()}
              className="font-ui text-sm font-medium text-slate-300 border border-gold-500/12 hover:border-gold-400/60 rounded-lg px-4 py-2 transition-colors">
              Search
            </button>
          </div>

          <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 overflow-hidden">
            {loadingUsers ? (
              <div className="min-h-[20vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-slate-400 animate-spin" /></div>
            ) : users.length === 0 ? (
              <p data-testid="admin-users-empty" className="font-read text-sm text-slate-500 p-6">No users found.</p>
            ) : (
              <div className="divide-y divide-gold-500/12">
                {users.map((u) => (
                  <div key={u.user_id} data-testid={`admin-user-row-${u.email}`} className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-ui text-sm text-slate-100 truncate">{u.email}</span>
                        {u.role === "admin" && <span className="font-ui text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold-400/15 text-slate-200">admin</span>}
                        <span className={`font-ui text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${u.plan === "Unlimited" ? "bg-emerald-500/15 text-emerald-300" : "bg-navy-800 text-slate-400"}`}>{u.plan}</span>
                      </div>
                      <div className="font-ui text-xs text-slate-500 mt-1">
                        {u.credits} credit{u.credits === 1 ? "" : "s"} · {u.publish_credits} publish · {u.books_count} book{u.books_count === 1 ? "" : "s"} · {u.preview_count} previews · joined {fmtDate(u.created_at)}
                        {u.plan === "Unlimited" && u.subscription_expires ? ` · until ${fmtDate(u.subscription_expires)}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      <button data-testid={`credited-toggle-${u.email}`} disabled={!!grantingId} onClick={() => toggleCredited(u)}
                        className={`flex items-center gap-1.5 font-ui text-xs rounded-lg px-2.5 py-1.5 border transition-colors disabled:opacity-40 ${u.credited ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "text-slate-400 border-gold-500/12 hover:border-gold-500/20"}`}>
                        {u.credited ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />} Credited
                      </button>
                      <button data-testid={`grant-credit-1-${u.email}`} disabled={!!grantingId} onClick={() => grant(u.user_id, "credits", 1, "+1 credit")}
                        className="font-ui text-xs text-emerald-300 border border-emerald-500/30 hover:border-emerald-400 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40">+1 credit</button>
                      <button data-testid={`grant-credit-5-${u.email}`} disabled={!!grantingId} onClick={() => grant(u.user_id, "credits", 5, "+5 credits")}
                        className="font-ui text-xs text-emerald-300 border border-emerald-500/30 hover:border-emerald-400 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40">+5</button>
                      <button data-testid={`grant-month-${u.email}`} disabled={!!grantingId} onClick={() => grant(u.user_id, "subscription_days", 31, "Comped 1 month")}
                        className="font-ui text-xs text-slate-200 border border-gold-400/40 hover:border-gold-300 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40">Comp 1mo</button>
                      <button data-testid={`grant-year-${u.email}`} disabled={!!grantingId} onClick={() => grant(u.user_id, "subscription_days", 365, "Comped 1 year")}
                        className="font-ui text-xs text-slate-200 border border-gold-400/40 hover:border-gold-300 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40">Comp 1yr</button>
                      {u.plan === "Unlimited" && (
                        <button data-testid={`revoke-sub-${u.email}`} disabled={!!grantingId} onClick={() => grant(u.user_id, "revoke_subscription", 0, "Unlimited removed")}
                          className="font-ui text-xs text-red-300 border border-red-500/30 hover:border-red-400 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40">Revoke</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {tab === "pipeline" && <PipelineKey />}
      {tab === "flags" && <ContinuityFlags />}
      {tab === "covertest" && <CoverBlindTest />}
      {tab === "inbox" && <AdminInbox />}

      {tab === "promo" && (
        <div className="space-y-6" data-testid="admin-promo">
          <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
            <h3 className="font-ui text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2"><Ticket className="w-4 h-4 text-slate-300" /> Create a promo code</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <input data-testid="promo-code-input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} />
              <select data-testid="promo-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls}>
                <option value="credits">Book credits</option>
                <option value="publish_credits">Publish credits</option>
                <option value="subscription_days">Unlimited days</option>
              </select>
              <input data-testid="promo-amount-input" type="number" min="1" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
              <input data-testid="promo-max-input" type="number" min="0" placeholder="Max uses (0=∞)" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className={inputCls} />
              <button data-testid="promo-create-button" onClick={createPromo} disabled={creating || !form.code.trim()}
                className="flex items-center justify-center gap-2 klyr-btn-metal disabled:opacity-50 font-ui text-sm font-semibold rounded-lg px-4 py-2 transition-[filter]">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5">
            <h3 className="font-ui text-sm font-semibold text-slate-200 mb-3">Active codes</h3>
            {promos.length === 0 ? (
              <p data-testid="promo-empty" className="font-read text-sm text-slate-500 py-4">No promo codes yet.</p>
            ) : (
              <div className="divide-y divide-gold-500/12">
                {promos.map((p) => (
                  <div key={p.code} data-testid={`promo-row-${p.code}`} className="flex items-center justify-between gap-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono2 text-sm font-bold px-2.5 py-1 rounded ${p.active ? "bg-gold-400/15 text-slate-200" : "bg-navy-800 text-slate-500 line-through"}`}>{p.code}</span>
                      <span className="font-ui text-xs text-slate-400">
                        {p.amount} {p.kind === "credits" ? "book credit(s)" : p.kind === "publish_credits" ? "publish credit(s)" : "Unlimited day(s)"}
                        {" · "}{p.redeemed_count}{p.max_redemptions ? `/${p.max_redemptions}` : ""} used
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`promo-toggle-${p.code}`} onClick={() => toggle(p.code)} title={p.active ? "Disable" : "Enable"}
                        className={`p-2 rounded-lg border ${p.active ? "border-emerald-500/40 text-emerald-300" : "border-gold-500/12 text-slate-500"} hover:opacity-80 transition-opacity`}>
                        {p.active ? <CheckCircle2 className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                      <button data-testid={`promo-delete-${p.code}`} onClick={() => remove(p.code)}
                        className="p-2 rounded-lg border border-gold-500/12 text-slate-500 hover:border-red-500/50 hover:text-red-300 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
